<script lang="ts">
    import type { ParticipantInput } from '$gql/graphql';
    import ParticipantProfileLinkWithLoader from '$components/ParticipantProfileLink/ParticipantProfileLinkWithLoader.svelte';
    import { getContainer } from '$components/Context/ContainerContext';

    export let text: string | null | undefined;

    export let skipMultiLineBreaks = false;

    const { urlHelper } = getContainer();

    const getParticipantInput = (
        identifier: string | undefined,
    ): ParticipantInput | undefined => {
        if (identifier != null) {
            const [type, id] = identifier.split('-');
            if (type === 'User') return { userId: parseInt(id ?? '0') };
            else if (type === 'Company') return { companyId: parseInt(id ?? '0') };
        }
    };

    const REGEX_SPLIT = /(\r\n|\r|\n|@\[[A-zÀ-ú0-9\s,'\-+./()&]+]\(\w+-\d+\))/g;
    const REGEX_HASHTAG = /^#[A-zÀ-ú0-9-]+$/g;
    const REGEX_URL =
        /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/gi;
    const REGEX_NEWLINE = /^(\r\n|\r|\n)$/;
    const REGEX_MENTION = /@\[(.+)]\((.+)\)/;

    type MentionResultType = {
        tag: string;
        text: string | number;
        props: {
            class?: string | null;
            rel?: string | null;
            target?: string | null;
            href?: string | null;
            'data-sveltekit-reload'?: true | '' | 'off' | null | undefined;
        };
        participant?: ParticipantInput;
    };

    $: tokens = reactMentionText(text);

    export function reactMentionText(
        text: string | number | null | undefined,
    ): MentionResultType[] {
        if (typeof text === 'number') {
            return [{ tag: 'span', text, props: {} }];
        } else if (typeof text !== 'string') {
            return [{ tag: 'span', text: '', props: {} }];
        }

        let textInput = text.replaceAll(/(^\s+|\s+$)/g, '');

        if (skipMultiLineBreaks) {
            textInput = textInput.replaceAll(/\n{3,}/g, '\n\n');
        }

        const tokens = textInput.split(REGEX_SPLIT);

        return tokens.flatMap((token: string) => {
            if (!token) {
                return { tag: 'span', text: '', props: {} };
            }
            if (token.match(REGEX_NEWLINE)) {
                return { tag: 'br', text: '', props: {} };
            }
            if (token.match(REGEX_HASHTAG)) {
                return hashTagWrapper(token);
            }
            let matches = token.match(REGEX_MENTION);
            if (matches) {
                let [, name, id] = matches;

                return {
                    tag: 'a',
                    text: `@${name}`,
                    props: {
                        href: '',
                        rel: 'noreferrer',
                        target: '_blank',
                        class: 'cursor-pointer text-link hover:text-link-hover break-words',
                    },
                    participant: getParticipantInput(id),
                };
            }

            return handleUrls(token);
        });
    }

    function handleUrls(str: string): MentionResultType[] {
        let tokens = str.split(/(\s+)/g);
        return tokens.flatMap((word: string): MentionResultType | MentionResultType[] => {
            if (REGEX_URL.test(word)) {
                return {
                    tag: 'a',
                    text: word,
                    props: {
                        href: word,
                        rel: 'noreferrer',
                        target: '_blank',
                        class: 'cursor-pointer text-link hover:text-link-hover break-all',
                    },
                };
            }

            if (REGEX_HASHTAG.test(word)) {
                return hashTagWrapper(word);
            }

            return [
                {
                    tag: 'span',
                    text: word,
                    props: { class: word.length > 30 ? 'break-all' : undefined },
                },
            ];
        });
    }

    function hashTagWrapper(hashTag: string): MentionResultType {
        return {
            tag: 'a',
            text: hashTag,
            props: {
                href: urlHelper().newsfeedHashtag(hashTag.replace('#', '')),
                'data-sveltekit-reload': 'off',
                rel: 'noreferrer',
                class: 'cursor-pointer text-link hover:text-link-hover break-words',
            },
        };
    }
</script>

{#each tokens as token}
    {#if token.tag === 'br' || token.tag === 'BR'}
        <br />
    {:else if token.participant}
        <ParticipantProfileLinkWithLoader
            withCard
            id={token.participant}
            label={`${token.text}`}
            class={token.props?.class ?? ''}
        />
    {:else}
        <svelte:element this={token.tag} {...token.props}>{token.text}</svelte:element>
    {/if}
{/each}
